import { Fragment } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import UserMenu from '@/shared/components/layout/UserMenu'
import { useAuth } from '@/features/auth/hooks/useAuth'

interface Props {
  isDashboard: boolean
}

/** Breadcrumb label per path segment. A segment with no entry (an id) renders as-is in mono. */
const routeLabels: Record<string, string> = {
  bos:         'BOS',
  uploads:     'Uploads',
  mapping:     'Client Mapping',
  entities:    'Entity history',
  exceptions:  'Exceptions',
  layout:      'Layout',
  billing:     'Billing',
  'fee-lines': 'Fee lines',
  unbilled:    'Unbilled',
  suggestions: 'Fee change suggestions',
  forecast:    'Forecast',
  history:     'History',
  publish:     'Publish',
  reference:   'Reference',
  data:        'Reference data',
  services:    'Service mapping',
  items:       'QBO items',
  buckets:     'Bucket tree',
  actuals:     'QBO actuals',
  admin:       'Admin',
  users:       'Users & roles',
  logs:        'Logs',
  me:          'My profile',
  new:         'New',
}

/** Top-level segments that group modules by Home category but have no page of their own. */
const categoryBySegment: Record<string, string> = {
  bos:     'BOS',
  billing: 'Billing',
  reference: 'Reference',
  admin:   'Administration',
}

/** Segments with no page of their own that should resolve to a real page instead. */
const segmentRedirects: Record<string, { label: string; href: string }> = {
  entities: { label: 'Client Mapping', href: '/bos/mapping' },
}

export default function Header({ isDashboard }: Props) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const segments = pathname.split('/').filter(Boolean)

  // ── Shared brand block ────────────────────────────────────────────────────
  const Brand = () => (
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <div className="h-7 sm:h-8 flex items-center shrink-0">
        <img src="/logo-hc_global_02.png" alt="HC Global"
          className="h-full w-auto object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
      </div>
      <span className="text-gray-300 select-none hidden xs:inline">|</span>
      <span className="text-base sm:text-xl font-light text-gray-500 select-none tracking-wide truncate hidden sm:block">
        Billing <span className="font-semibold text-primary">Portal</span>
      </span>
      <span className="text-sm font-semibold text-primary select-none sm:hidden">Billing</span>
    </div>
  )

  // ── Shared right user block ───────────────────────────────────────────────
  const UserBlock = () => (
    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
      <UserMenu
        name={user?.name ?? user?.email ?? ''}
        email={user?.email ?? ''}
        onProfile={() => navigate('/me')}
        onLogout={logout}
      />
    </div>
  )

  // ── Home header ───────────────────────────────────────────────────────────
  if (isDashboard) {
    return (
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-gray-200 gap-3">
        <Brand />
        <UserBlock />
      </header>
    )
  }

  // ── Sub-page header — breadcrumb ──────────────────────────────────────────
  return (
    <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-gray-200 gap-3">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-gray-500 min-w-0 overflow-hidden">
        <Link to="/" className="hover:text-primary transition-colors shrink-0">Home</Link>
        {segments.map((segment, i) => {
          const isLast = i === segments.length - 1
          const redirect = segmentRedirects[segment]
          const category = categoryBySegment[segment]
          const label = redirect?.label ?? routeLabels[segment]
          const to = redirect?.href ?? (category ? `/?category=${encodeURIComponent(category)}` : `/${segments.slice(0, i + 1).join('/')}`)
          return (
            <Fragment key={to}>
              <ChevronRight size={14} className="shrink-0" />
              {isLast ? (
                <span className={`text-gray-800 font-medium truncate ${label ? '' : 'font-mono'}`}>
                  {label ?? segment}
                </span>
              ) : (
                <Link to={to} className="hover:text-primary transition-colors hidden sm:inline shrink-0">
                  {label ?? segment}
                </Link>
              )}
            </Fragment>
          )
        })}
      </nav>

      <UserBlock />
    </header>
  )
}
